import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import { showDialog, closeDialog } from 'stores/dialogSlice';
import { localeIds } from 'locals';
import { ApplicationDomain } from 'models/Application';
import { setExceptionError } from 'stores/errorSlice';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { Splash } from 'views/components/molecules/Splash';
import { AvailableApplication } from 'models/Device/type';
import { AppSearchPageLayout } from 'views/layouts/AppSearchPageLayout';

interface AppSearchPageParams {
  userName: string;
  groupName: string;
  packageName: string;
  revision: string;
}

export const AppSearchPage: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const [applications, setApplications] = useState<AvailableApplication[]>([]);
  const {
    userName,
    groupName,
    packageName,
    revision,
  } = useParams<AppSearchPageParams>();
  const applicationDomain = useMemo(() => {
    const ownerType = userName ? 'users' : 'organizations';
    const ownerName = userName ? userName : groupName;

    return new ApplicationDomain(
      ownerType,
      ownerName,
      packageName,
      revision,
      device
    );
  }, [device, groupName, packageName, revision, userName]);

  const handleCloseDialogAndGoBack = useCallback(() => {
    dispatch(closeDialog());
    history.goBack();
  }, [history, dispatch]);

  const handleSearchInputChange = useCallback(
    (input: string) => {
      const pattern = `${
        input == null || input == undefined ? '' : input
      }`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      setApplications((state) => {
        try {
          const regex = new RegExp(pattern, 'i');
          return state.map((app) => {
            const result = regex.test(
              `#${app.revision}
              ${app.versionName}
              ${app.title}
              ${app.releaseNote}`
            );
            app.visible = result;
            return app;
          });
        } catch {
          return applications;
        }
      });
    },
    [applications]
  );

  const handleAlertWithGoBack = useCallback(
    (id: string) => {
      dispatch(
        showDialog({
          content: intl.formatMessage({ id }),
          defaultAction: {
            label: intl.formatMessage({ id: localeIds.dialog.ok }),
            callback: handleCloseDialogAndGoBack,
          },
        })
      );
    },
    [dispatch, handleCloseDialogAndGoBack, intl]
  );

  const getApplications = useCallback(
    async (maxRevision: number | undefined): Promise<number | undefined> => {
      return await applicationDomain
        .applications(maxRevision)
        .then((result) => {
          if (result.isFailure()) {
            handleAlertWithGoBack(result.value.localeId());
            return undefined;
          }

          const [availableApplications, nextMaxRevision] = result.value;
          setApplications((state) => {
            return state.concat(availableApplications);
          });
          return nextMaxRevision;
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
          return undefined;
        });
    },
    [applicationDomain, handleAlertWithGoBack, dispatch]
  );

  const fetchAllVersions = useCallback(async (): Promise<void> => {
    let maxRequestCount = 100;
    let maxRevision: number | undefined = undefined;

    do {
      maxRevision = await getApplications(maxRevision);
    } while (maxRevision && maxRequestCount-- > 0);
  }, [getApplications]);

  useEffect(() => {
    fetchAllVersions();
  }, [fetchAllVersions]);

  return applications ? (
    <DefaultTemplate
      headerText={intl.formatMessage({
        id: localeIds.appSearchPageLayout.title,
      })}
      onBack={(): void => {
        history.goBack();
      }}
    >
      <AppSearchPageLayout
        applications={applications}
        onSearchInputChange={handleSearchInputChange}
        searchFormPlaceholder={intl.formatMessage({
          id: localeIds.appSearchPageLayout.inputPlaceholder,
        })}
      />
    </DefaultTemplate>
  ) : (
    <DefaultTemplate headerText="">
      <Splash />
    </DefaultTemplate>
  );
};
